import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, orderBy, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { markSeen } from "../hooks/useUnseenBadges";
import { deleteCalendarEvent } from "../lib/cascadeDelete";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import { occursOnDate, toISODate } from "../lib/recurrence";
import type { CalendarEvent, CalendarEventComment, EventCategory, EventRecurrence, Todo } from "../types";

const getIcsFeedToken = httpsCallable<{ coupleId: string; regenerate?: boolean }, { token: string }>(functions, "getIcsFeedToken");
const ICS_FEED_BASE = "https://europe-west1-wir-app-huebbedigital.cloudfunctions.net/icsFeed";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const CATEGORY_COLORS = ["#6e2d49", "#c08a2e", "#2f6e52", "#2f5f8f", "#bf3a42", "#7a5c3e"];
const RECURRENCE_LABELS: { value: EventRecurrence; label: string }[] = [
  { value: "none", label: "Einmalig" },
  { value: "weekly", label: "Wöchentlich" },
  { value: "monthly", label: "Monatlich" },
  { value: "yearly", label: "Jährlich" },
];
const RECURRENCE_SHORT: Record<Exclude<EventRecurrence, "none">, string> = {
  weekly: "wöchentlich",
  monthly: "monatlich",
  yearly: "jährlich",
};
const NONE = "__none__";

const toISO = toISODate;

function buildGrid(monthStart: Date): Date[] {
  const firstWeekday = (monthStart.getDay() + 6) % 7; // Monday = 0
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - firstWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

const MAX_LANES = 3;
const LANE_PITCH = 14; // px per bar row, including its gap

interface Segment {
  ev: CalendarEvent;
  colStart: number; // 0-6, Monday first
  colEnd: number; // 0-6, inclusive
  capStart: boolean; // rounded/visible at this segment's left edge — true only on the event's real start day
  capEnd: boolean;
}

// One week-row's worth of event bars: a continuous multi-day event becomes
// one segment clipped to this week (flat-edged where it continues into the
// row above/below); a recurring event's occurrences are independent
// single-day segments, since — unlike a date range — they don't form a
// continuous span (see CalendarEvent.recurrence).
function buildWeekSegments(weekDates: string[], items: CalendarEvent[]): Segment[] {
  const segs: Segment[] = [];
  for (const ev of items) {
    if (!ev.recurrence || ev.recurrence === "none") {
      if (ev.endDate < weekDates[0] || ev.startDate > weekDates[6]) continue;
      const segStartDate = ev.startDate < weekDates[0] ? weekDates[0] : ev.startDate;
      const segEndDate = ev.endDate > weekDates[6] ? weekDates[6] : ev.endDate;
      segs.push({
        ev,
        colStart: weekDates.indexOf(segStartDate),
        colEnd: weekDates.indexOf(segEndDate),
        capStart: segStartDate === ev.startDate,
        capEnd: segEndDate === ev.endDate,
      });
    } else {
      weekDates.forEach((iso, i) => {
        if (occursOnDate(ev, iso)) segs.push({ ev, colStart: i, colEnd: i, capStart: true, capEnd: true });
      });
    }
  }
  return segs;
}

// Greedy lane packing (first-fit by start column) so overlapping bars stack
// instead of colliding; anything beyond MAX_LANES is dropped from the bar
// view and counted per-day instead, surfaced as a small "+N" underneath.
function packWeekLanes(segs: Segment[]): { placed: (Segment & { lane: number })[]; overflowByCol: number[] } {
  const sorted = segs
    .slice()
    .sort((a, b) => a.colStart - b.colStart || b.colEnd - b.colStart - (a.colEnd - a.colStart));
  const laneEnds: number[] = [];
  const placed: (Segment & { lane: number })[] = [];
  const overflow: Segment[] = [];
  for (const seg of sorted) {
    let lane = laneEnds.findIndex((end) => end < seg.colStart);
    if (lane === -1) lane = laneEnds.length;
    if (lane >= MAX_LANES) {
      overflow.push(seg);
      continue;
    }
    laneEnds[lane] = seg.colEnd;
    placed.push({ ...seg, lane });
  }
  const overflowByCol = Array(7).fill(0);
  for (const seg of overflow) {
    for (let c = seg.colStart; c <= seg.colEnd; c++) overflowByCol[c]++;
  }
  return { placed, overflowByCol };
}

export function Calendar() {
  const { user } = useAuth();
  const { couple, profile, partner } = useCouple();
  const { items, loading, add, patch } = useCoupleCollection<CalendarEvent>(
    couple?.id ?? null,
    "calendarEvents",
    orderBy("startDate", "asc")
  );
  const { items: categories, add: addCategory } = useCoupleCollection<EventCategory>(
    couple?.id ?? null,
    "eventCategories",
    orderBy("createdAt", "asc")
  );
  // Direct Firestore call rather than useCoupleCollection — avoids
  // subscribing to the entire todos collection just to patch the one linked
  // todo when its calendar event is edited or removed (same reasoning as
  // the calendar-event side of this in Todos.tsx).
  const coupleIdForTodoSync = couple?.id ?? null;
  async function patchLinkedTodo(id: string, data: Partial<Todo>) {
    await updateDoc(doc(db, "couples", coupleIdForTodoSync!, "todos", id), data);
  }
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  useEffect(() => {
    markSeen("calendar");
    return () => markSeen("calendar");
  }, []);

  const today = useMemo(() => new Date(), []);
  const [monthStart, setMonthStart] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(() => toISO(today));
  // The day's full event list (comment button included, not capped at
  // MAX_LANES like the month-grid bars) lives below the grid — tapping a
  // date used to leave it to scroll there manually, so "sieht man nicht
  // alle Termine" for a busy day's bars, and the only reachable action
  // from the grid itself was editing. Scrolling this into view on tap
  // means the full list (and each event's comment button) is immediately
  // visible instead.
  const dayEventsRef = useRef<HTMLDivElement>(null);
  function selectDay(iso: string) {
    setSelected(iso);
    dayEventsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  // The date being edited in the form — kept separate from `selected` (which
  // drives the Heute/Nächste-Tage vs. single-day view below) so that editing
  // an event surfaced from "Die nächsten Tage" doesn't yank the whole page
  // away from the overview just because its date differs from `selected`.
  const [formDate, setFormDate] = useState(() => toISO(today));
  const [endDate, setEndDate] = useState("");
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [recurrence, setRecurrence] = useState<EventRecurrence>("none");
  const [recurrenceCount, setRecurrenceCount] = useState(""); // empty = repeats indefinitely
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState(CATEGORY_COLORS[0]);
  const [submitting, setSubmitting] = useState(false);

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  async function openSubscribe() {
    setSubscribeOpen(true);
    if (feedUrl || !couple) return;
    // Lazily generated on first tap rather than at pairing time — most
    // couples will never use this, no reason to mint a secret token for
    // everyone up front.
    if (couple.icsToken) {
      setFeedUrl(`${ICS_FEED_BASE}?coupleId=${couple.id}&token=${couple.icsToken}`);
      return;
    }
    setSubscribeLoading(true);
    try {
      const res = await getIcsFeedToken({ coupleId: couple.id });
      setFeedUrl(`${ICS_FEED_BASE}?coupleId=${couple.id}&token=${res.data.token}`);
    } finally {
      setSubscribeLoading(false);
    }
  }

  // Invalidates a link that may have been shared with the wrong person,
  // or just wants refreshing — the old URL stops working the moment the
  // new token is stored, since icsFeed only ever checks the current one.
  async function resetFeed() {
    if (!couple) return;
    setSubscribeLoading(true);
    try {
      const res = await getIcsFeedToken({ coupleId: couple.id, regenerate: true });
      setFeedUrl(`${ICS_FEED_BASE}?coupleId=${couple.id}&token=${res.data.token}`);
    } finally {
      setSubscribeLoading(false);
    }
  }

  const [commentEventId, setCommentEventId] = useState<string | null>(null);
  const commentEvent = items.find((ev) => ev.id === commentEventId) ?? null;

  // Arriving from the Neuigkeiten bell (?date=...&comment=...) — jump to
  // that exact day (and scroll its event list into view) and, for a
  // comment-type entry, straight into that event's comment thread. Runs
  // once on mount; deep links aren't meant to keep re-applying afterward.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const dateParam = searchParams.get("date");
    const commentParam = searchParams.get("comment");
    if (dateParam) {
      setSelected(dateParam);
      const d = new Date(dateParam + "T00:00");
      setMonthStart(new Date(d.getFullYear(), d.getMonth(), 1));
      requestAnimationFrame(() => dayEventsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
    if (commentParam) setCommentEventId(commentParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  function toggleFilter(id: string) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filteredItems = useMemo(() => {
    if (activeFilters.size === 0) return items;
    return items.filter((ev) => activeFilters.has(ev.categoryId ?? NONE));
  }, [items, activeFilters]);

  const grid = useMemo(() => buildGrid(monthStart), [monthStart]);
  const weeks = useMemo(() => {
    const out: Date[][] = [];
    for (let i = 0; i < grid.length; i += 7) out.push(grid.slice(i, i + 7));
    return out;
  }, [grid]);

  // Keyed off the visible 42-day grid rather than each event's own date
  // range — the only way to show a weekly/monthly recurring event's
  // instances without expanding it into unbounded future documents.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const d of grid) {
      const iso = toISO(d);
      const dayEvents = filteredItems.filter((ev) => occursOnDate(ev, iso));
      if (dayEvents.length > 0) map.set(iso, dayEvents);
    }
    return map;
  }, [filteredItems, grid]);

  const todayISO = toISO(today);
  const isViewingToday = selected === todayISO;
  const selectedEvents = (eventsByDate.get(selected) ?? [])
    .slice()
    .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""));

  const todayEvents = useMemo(
    () =>
      filteredItems
        .filter((ev) => occursOnDate(ev, todayISO))
        .slice()
        .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? "")),
    [filteredItems, todayISO]
  );

  // Grouped by date rather than a flat list, scanning day-by-day (not just
  // the currently browsed month's grid) so this stays correct even while
  // the month grid above has been navigated elsewhere.
  const upcomingGroups = useMemo(() => {
    const groups: { iso: string; events: CalendarEvent[] }[] = [];
    for (let i = 1; i <= 60 && groups.length < 8; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const iso = toISO(d);
      const dayEvents = filteredItems.filter((ev) => occursOnDate(ev, iso));
      if (dayEvents.length > 0) {
        groups.push({ iso, events: dayEvents.slice().sort((a, b) => (a.time ?? "").localeCompare(b.time ?? "")) });
      }
    }
    return groups;
  }, [filteredItems, today]);

  function changeMonth(delta: number) {
    setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + delta, 1));
  }

  function openAdd() {
    setEditingId(null);
    setTitle("");
    setFormDate(selected);
    setEndDate("");
    setTime("");
    setEndTime("");
    setNotes("");
    setCategoryId(null);
    setRecurrence("none");
    setRecurrenceCount("");
    setNewCatOpen(false);
    setSheetOpen(true);
  }

  function openEdit(ev: CalendarEvent) {
    setEditingId(ev.id);
    setTitle(ev.title);
    setFormDate(ev.startDate);
    setEndDate(ev.endDate !== ev.startDate ? ev.endDate : "");
    setTime(ev.time ?? "");
    setEndTime(ev.endTime ?? "");
    setNotes(ev.notes);
    setCategoryId(ev.categoryId);
    setRecurrence(ev.recurrence ?? "none");
    setRecurrenceCount(ev.recurrenceCount != null ? String(ev.recurrenceCount) : "");
    setNewCatOpen(false);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setEditingId(null);
  }

  // Removing the event only unlinks the todo (clears linkedEventId) rather
  // than deleting it — the task itself may still need doing even once it's
  // off the calendar, unlike the other direction (deleting the todo removes
  // its event too, since that only ever existed because of the todo).
  async function handleRemoveEvent(ev: CalendarEvent) {
    if (ev.linkedTodoId) await patchLinkedTodo(ev.linkedTodoId, { linkedEventId: null }).catch(() => {});
    await deleteCalendarEvent(couple!.id, ev.id);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Without this, a quick double-tap before the Sheet closes re-enters
    // this handler and creates the event twice — each a real document, each
    // separately triggering the partner's push notification (see TopBar.tsx).
    if (submitting || !title.trim() || !user || !profile) return;
    setSubmitting(true);
    try {
      // A recurring event is always single-day — its "span" each time it
      // occurs is just that one day, so an end date is meaningless for it.
      const effectiveEndDate = recurrence === "none" ? endDate || formDate : formDate;
      const isMultiDay = effectiveEndDate !== formDate;
      const parsedCount = recurrence !== "none" ? parseInt(recurrenceCount, 10) : NaN;
      const data = {
        title: title.trim(),
        startDate: formDate,
        endDate: effectiveEndDate,
        time: isMultiDay ? null : time || null,
        endTime: isMultiDay ? null : endTime || null,
        notes: notes.trim(),
        categoryId,
        recurrence,
        recurrenceCount: Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : null,
      };
      if (editingId) {
        await patch(editingId, { ...data, lastEditedByUid: user.uid, lastEditedByName: profile.displayName });
        // Keep a linked todo's text/due date in sync — the start date (not
        // the possibly-later end date) is what maps to "fällig am" there.
        const linkedTodoId = items.find((ev) => ev.id === editingId)?.linkedTodoId;
        if (linkedTodoId) {
          await patchLinkedTodo(linkedTodoId, { text: title.trim(), dueDate: formDate }).catch(() => {});
        }
      } else {
        await add({ ...data, createdByUid: user.uid, createdByName: profile.displayName });
      }
      closeSheet();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateCategory() {
    if (!newCatName.trim() || !user || !profile) return;
    await addCategory({
      name: newCatName.trim(),
      color: newCatColor,
      createdByUid: user.uid,
      createdByName: profile.displayName,
    });
    setNewCatName("");
    setNewCatOpen(false);
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Kalender</h1>
        <button
          onClick={openSubscribe}
          aria-label="Kalender abonnieren"
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-ink-400 dark:text-night-faint"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
            <rect x="4" y="5" width="16" height="16" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" strokeLinecap="round" />
            <path d="M9 17h.01M12 17h.01M15 17h.01" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Eure gemeinsamen Termine.</p>

      {categories.length > 0 && (
        <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => toggleFilter(c.id)}
              className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                activeFilters.has(c.id) ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
              }`}
              style={activeFilters.has(c.id) ? { backgroundColor: c.color, borderColor: c.color, color: "#fdf6f3" } : undefined}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: activeFilters.has(c.id) ? "#fdf6f3" : c.color }} />
              {c.name}
            </button>
          ))}
          <button
            onClick={() => toggleFilter(NONE)}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeFilters.has(NONE) ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
            }`}
            style={activeFilters.has(NONE) ? { backgroundColor: "#a6949b", borderColor: "#a6949b", color: "#fdf6f3" } : undefined}
          >
            Ohne Kategorie
          </button>
        </div>
      )}

      <div className="card mt-4 rounded-3xl p-4 pb-3">
        <div className="flex items-center justify-between px-1">
          <button onClick={() => changeMonth(-1)} aria-label="Vorheriger Monat" className="flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:text-plum-700 dark:text-night-faint dark:hover:text-night-accent">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
              <path d="m15 6-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <p className="font-display text-base font-semibold text-plum-900 dark:text-night-accent">
            {monthStart.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
          </p>
          <button onClick={() => changeMonth(1)} aria-label="Nächster Monat" className="flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:text-plum-700 dark:text-night-faint dark:hover:text-night-accent">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
              <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="mt-2 grid grid-cols-7 text-center">
          {WEEKDAYS.map((w) => (
            <span key={w} className="text-[11px] font-medium uppercase tracking-wide text-ink-400 dark:text-night-faint">
              {w}
            </span>
          ))}
        </div>

        <div className="mt-0.5 space-y-0.5">
          {weeks.map((week, wi) => {
            const weekDates = week.map(toISO);
            const { placed, overflowByCol } = packWeekLanes(buildWeekSegments(weekDates, filteredItems));
            const hasOverflow = overflowByCol.some((n) => n > 0);
            return (
              <div key={wi}>
                <div className="grid grid-cols-7">
                  {week.map((d) => {
                    const iso = toISO(d);
                    const inMonth = d.getMonth() === monthStart.getMonth();
                    const isToday = iso === todayISO;
                    const isSelected = iso === selected;
                    return (
                      <button key={iso} onClick={() => selectDay(iso)} className="flex items-center justify-center">
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-sm transition-colors ${
                            isSelected
                              ? "bg-plum-700 font-semibold text-cream-50"
                              : isToday
                                ? "font-semibold text-plum-700 ring-1 ring-plum-600 dark:text-night-accent dark:ring-night-accent"
                                : inMonth
                                  ? "text-ink-900 dark:text-night-text"
                                  : "text-ink-400/50 dark:text-night-faint/50"
                          }`}
                        >
                          {d.getDate()}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="relative" style={{ height: `${MAX_LANES * LANE_PITCH}px` }}>
                  {placed.map((seg) => {
                    const cat = seg.ev.categoryId ? categoryById.get(seg.ev.categoryId) : null;
                    const color = cat?.color ?? "#8c3a53";
                    return (
                      <button
                        key={`${seg.ev.id}-${seg.colStart}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(seg.ev);
                        }}
                        className={`absolute top-0 flex items-center px-1 text-left text-[9px] font-medium leading-none text-cream-50 ${
                          seg.capStart ? "rounded-l" : ""
                        } ${seg.capEnd ? "rounded-r" : ""}`}
                        style={{
                          left: `${(seg.colStart / 7) * 100}%`,
                          width: `${((seg.colEnd - seg.colStart + 1) / 7) * 100}%`,
                          top: `${seg.lane * LANE_PITCH}px`,
                          height: "12px",
                          backgroundColor: color,
                        }}
                      >
                        <span className="truncate">{seg.ev.title}</span>
                      </button>
                    );
                  })}
                </div>

                {hasOverflow && (
                  <div className="grid grid-cols-7">
                    {overflowByCol.map((n, i) => (
                      <span key={i} className="text-center text-[8px] leading-tight text-ink-400 dark:text-night-faint">
                        {n > 0 ? `+${n}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div ref={dayEventsRef}>
      {isViewingToday ? (
        <>
          <section className="mt-6">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Heute</h2>
            {loading ? (
              <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
            ) : todayEvents.length === 0 ? (
              <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">Keine Termine heute.</p>
            ) : (
              <ul className="mt-3 space-y-2.5">
                {todayEvents.map((ev) => (
                  <EventRow key={ev.id} ev={ev} cat={ev.categoryId ? categoryById.get(ev.categoryId) : null} onEdit={() => openEdit(ev)} onRemove={() => handleRemoveEvent(ev)} onComment={() => setCommentEventId(ev.id)} />
                ))}
              </ul>
            )}
          </section>

          <section className="mt-6">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Die nächsten Tage</h2>
            {!loading && upcomingGroups.length === 0 ? (
              <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">Nichts in Sicht.</p>
            ) : (
              <div className="mt-3 space-y-5">
                {upcomingGroups.map((g) => (
                  <div key={g.iso}>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">
                      {new Date(g.iso + "T00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long" })}
                    </p>
                    <ul className="space-y-2.5">
                      {g.events.map((ev) => (
                        <EventRow key={ev.id} ev={ev} cat={ev.categoryId ? categoryById.get(ev.categoryId) : null} onEdit={() => openEdit(ev)} onRemove={() => handleRemoveEvent(ev)} onComment={() => setCommentEventId(ev.id)} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">
            {new Date(selected + "T00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
          </h2>

          {loading ? (
            <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
          ) : selectedEvents.length === 0 ? (
            <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">Keine Termine an diesem Tag.</p>
          ) : (
            <ul className="mt-3 space-y-2.5">
              {selectedEvents.map((ev) => (
                <EventRow key={ev.id} ev={ev} cat={ev.categoryId ? categoryById.get(ev.categoryId) : null} onEdit={() => openEdit(ev)} onRemove={() => handleRemoveEvent(ev)} onComment={() => setCommentEventId(ev.id)} />
              ))}
            </ul>
          )}
        </section>
      )}
      </div>

      <Fab onClick={openAdd} label="Termin hinzufügen" />

      <Sheet open={sheetOpen} onClose={closeSheet} title={editingId ? "Termin bearbeiten" : "Neuer Termin"}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <div>
            <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">Von</p>
            <input
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium text-ink-400 dark:text-night-faint">Wiederholung</p>
            <div className="flex gap-1.5">
              {RECURRENCE_LABELS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRecurrence(r.value)}
                  className={`flex-1 rounded-xl border px-2 py-2 text-xs font-medium ${
                    recurrence === r.value ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                  }`}
                  style={recurrence === r.value ? { backgroundColor: "#6e2d49", borderColor: "#6e2d49", color: "#fdf6f3" } : undefined}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {recurrence !== "none" && (
            <div>
              <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">Endet nach … Terminen (optional, sonst endlos)</p>
              <div className="flex gap-1.5">
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="z.B. 10"
                  value={recurrenceCount}
                  onChange={(e) => setRecurrenceCount(e.target.value)}
                  className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                {recurrenceCount && (
                  <button
                    type="button"
                    onClick={() => setRecurrenceCount("")}
                    aria-label="Ende zurücksetzen"
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          {recurrence === "none" && (
            <div>
              <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">Bis (optional, für mehrtägige Termine)</p>
              <div className="flex gap-1.5">
                <input
                  type="date"
                  value={endDate}
                  min={formDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                {endDate && (
                  // iOS's native date picker has no visible way to clear a
                  // value back to empty once set, so this button is the only
                  // way to get back to "kein Enddatum" on that platform.
                  <button
                    type="button"
                    onClick={() => setEndDate("")}
                    aria-label="Enddatum zurücksetzen"
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          {(recurrence !== "none" || !endDate || endDate === formDate) && (
            <div>
              <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">Uhrzeit (optional, von–bis)</p>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                <span className="flex-shrink-0 text-xs text-ink-400 dark:text-night-faint">bis</span>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={!time}
                  className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 disabled:opacity-50 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
              </div>
            </div>
          )}
          <input
            type="text"
            placeholder="Notiz (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />

          <div>
            <p className="mb-1.5 text-xs font-medium text-ink-400 dark:text-night-faint">Kategorie (optional)</p>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoryId(categoryId === c.id ? null : c.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                    categoryId === c.id ? "" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                  }`}
                  style={categoryId === c.id ? { backgroundColor: c.color, borderColor: c.color, color: "#fdf6f3" } : undefined}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: categoryId === c.id ? "#fdf6f3" : c.color }} />
                  {c.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setNewCatOpen((v) => !v)}
                className="rounded-full border border-dashed border-cream-200 px-3 py-1.5 text-xs font-medium text-ink-400 dark:border-night-border dark:text-night-faint"
              >
                + Neu
              </button>
            </div>

            {newCatOpen && (
              <div className="mt-2.5 space-y-2 rounded-xl bg-cream-100 p-3 dark:bg-night-surface-raised">
                <input
                  type="text"
                  placeholder="Kategoriename"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  className="h-10 w-full rounded-lg border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                <div className="flex items-center gap-2">
                  {CATEGORY_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewCatColor(c)}
                      aria-label={c}
                      className="h-6 w-6 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: c, boxShadow: newCatColor === c ? "0 0 0 2px white, 0 0 0 3.5px " + c : "none" }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={handleCreateCategory}
                    disabled={!newCatName.trim()}
                    className="ml-auto h-8 flex-shrink-0 rounded-lg bg-plum-700 px-3 text-xs font-semibold text-cream-50 disabled:opacity-50"
                  >
                    Erstellen
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!title.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Termin speichern"}
          </button>
        </form>
      </Sheet>

      <Sheet open={subscribeOpen} onClose={() => setSubscribeOpen(false)} title="Kalender abonnieren">
        <p className="text-sm text-ink-600 dark:text-night-muted">
          Einmal einrichten, dann tauchen eure Termine automatisch in eurem normalen Handy-Kalender auf — nur in diese
          Richtung (Wir → Handy-Kalender), nicht umgekehrt. Änderungen hier spiegeln sich innerhalb von ein paar
          Stunden; Löschen hier entfernt es auch dort.
        </p>
        {subscribeLoading || !feedUrl ? (
          <p className="mt-4 text-sm text-ink-400 dark:text-night-faint">Wird vorbereitet…</p>
        ) : (
          <SubscribeLinks feedUrl={feedUrl} onReset={resetFeed} />
        )}
      </Sheet>

      <CalendarCommentsSheet
        coupleId={couple?.id ?? null}
        eventId={commentEventId}
        eventTitle={commentEvent?.title ?? null}
        onClose={() => setCommentEventId(null)}
        meUid={user?.uid ?? null}
        meName={profile?.displayName ?? null}
        partnerName={partner?.displayName ?? null}
      />
    </main>
  );
}

function CalendarCommentsSheet({
  coupleId,
  eventId,
  eventTitle,
  onClose,
  meUid,
  meName,
  partnerName,
}: {
  coupleId: string | null;
  eventId: string | null;
  eventTitle: string | null;
  onClose: () => void;
  meUid: string | null;
  meName: string | null;
  partnerName: string | null;
}) {
  // Gated on eventId, same reasoning as Todos' comment sheet — only the
  // thread actually open gets a live subscription, not one per row.
  const { items: comments, loading, add, patch } = useCoupleCollection<CalendarEventComment>(
    eventId ? coupleId : null,
    `calendarEvents/${eventId}/comments`,
    orderBy("createdAt", "asc")
  );
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Only the comment's own author can edit it (also enforced in
  // firestore.rules — this is just the UI side of that).
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  useEffect(() => {
    setEditingCommentId(null);
    setDraft("");
  }, [eventId]);

  function startEdit(c: CalendarEventComment) {
    setEditingCommentId(c.id);
    setDraft(c.text);
  }

  function cancelEdit() {
    setEditingCommentId(null);
    setDraft("");
  }

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (submitting || !draft.trim() || !meUid || !meName) return;
    setSubmitting(true);
    try {
      if (editingCommentId) {
        await patch(editingCommentId, { text: draft.trim(), editedAt: serverTimestamp() });
        setEditingCommentId(null);
      } else {
        await add({ text: draft.trim(), createdByUid: meUid, createdByName: meName });
      }
      setDraft("");
    } finally {
      setSubmitting(false);
    }
  }

  function insertMention() {
    if (!partnerName) return;
    const tag = `@${partnerName}`;
    if (draft.includes(tag)) return;
    setDraft((prev) => (prev.trim() ? `${prev.trim()} ${tag} ` : `${tag} `));
  }

  return (
    <Sheet open={eventId !== null} onClose={onClose} title={eventTitle ?? "Kommentare"}>
      <div className="max-h-[50vh] space-y-3 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-ink-400 dark:text-night-faint">Noch keine Kommentare.</p>
        ) : (
          comments.map((c) => {
            const isMine = c.createdByUid === meUid;
            const bubble = (
              <p
                className={`mt-0.5 inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-left text-sm ${
                  isMine ? "bg-plum-700 text-cream-50" : "bg-cream-100 text-ink-900 dark:bg-night-surface-raised dark:text-night-text"
                }`}
              >
                {c.text}
                {c.editedAt && <span className={`ml-1.5 text-[10px] ${isMine ? "text-cream-50/60" : "text-ink-400 dark:text-night-faint"}`}>(bearbeitet)</span>}
              </p>
            );
            return (
              <div key={c.id} className={isMine ? "text-right" : "text-left"}>
                <p className="text-xs font-semibold text-ink-400 dark:text-night-faint">{c.createdByName}</p>
                {isMine ? (
                  <button type="button" onClick={() => startEdit(c)} aria-label="Kommentar bearbeiten">
                    {bubble}
                  </button>
                ) : (
                  bubble
                )}
              </div>
            );
          })
        )}
      </div>
      {partnerName && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={insertMention}
            className="flex-shrink-0 rounded-full border border-cream-200 px-2.5 py-1 text-xs font-medium text-plum-700 dark:border-night-border dark:text-night-accent"
          >
            @{partnerName}
          </button>
          <p className="text-xs text-ink-400 dark:text-night-faint">Erwähnt mit @{partnerName} benachrichtigt gezielt, auch wenn sonst aus.</p>
        </div>
      )}
      {editingCommentId && <p className="mt-3 text-xs font-medium text-plum-700 dark:text-night-accent">Kommentar wird bearbeitet…</p>}
      <form onSubmit={handleSend} className={editingCommentId ? "mt-1 flex gap-2" : "mt-2 flex gap-2"}>
        <input
          type="text"
          placeholder="z.B. Passt dir 18 Uhr?"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
        />
        {editingCommentId && (
          <button
            type="button"
            onClick={cancelEdit}
            aria-label="Bearbeiten abbrechen"
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <button
          type="submit"
          disabled={!draft.trim() || submitting}
          aria-label={editingCommentId ? "Kommentar speichern" : "Kommentar senden"}
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-cream-50 disabled:opacity-50"
        >
          {submitting ? (
            "…"
          ) : editingCommentId ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="h-5 w-5">
              <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </form>
    </Sheet>
  );
}

function SubscribeLinks({ feedUrl, onReset }: { feedUrl: string; onReset: () => void }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can be unavailable (older WebView, denied
      // permission) — the URL is still right there to select manually.
    }
  }
  return (
    <div className="mt-4 space-y-4">
      <a
        href={feedUrl.replace(/^https?:\/\//, "webcal://")}
        className="flex h-11 w-full items-center justify-center rounded-xl bg-plum-700 text-sm font-semibold text-cream-50"
      >
        Zum iPhone/Mac-Kalender hinzufügen
      </a>
      <div>
        <p className="mb-1.5 text-xs font-medium text-ink-400 dark:text-night-faint">
          Für Google Calendar: Einstellungen → Kalender hinzufügen → Per URL, diesen Link einfügen
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={feedUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="h-11 flex-1 truncate rounded-xl border border-cream-200 bg-white px-3 text-xs text-ink-600 outline-none dark:border-night-border dark:bg-night-surface-raised dark:text-night-muted"
          />
          <button
            onClick={copy}
            className="h-11 flex-shrink-0 rounded-xl border border-cream-200 px-3 text-xs font-semibold text-ink-900 dark:border-night-border dark:text-night-text"
          >
            {copied ? "Kopiert!" : "Kopieren"}
          </button>
        </div>
      </div>
      <button onClick={onReset} className="text-xs font-medium text-garnet-600">
        Link zurücksetzen (alter Link funktioniert danach nicht mehr)
      </button>
    </div>
  );
}

function EventRow({
  ev,
  cat,
  onEdit,
  onRemove,
  onComment,
}: {
  ev: CalendarEvent;
  cat: EventCategory | null | undefined;
  onEdit: () => void;
  onRemove: () => void;
  onComment: () => void;
}) {
  const multiDay = ev.endDate !== ev.startDate;
  return (
    <li className="card flex items-start gap-3 rounded-2xl p-4">
      {cat && <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: cat.color }} />}
      {ev.time && !multiDay && (
        <span className="mt-0.5 flex-shrink-0 rounded-full bg-plum-100 px-2 py-0.5 text-xs font-semibold text-plum-700 dark:bg-night-surface-raised dark:text-night-accent">
          {ev.time}
          {ev.endTime && <>–{ev.endTime}</>}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink-900 dark:text-night-text">{ev.title}</p>
        {multiDay && (
          <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">
            {new Date(ev.startDate + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
            {" – "}
            {new Date(ev.endDate + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
          </p>
        )}
        {ev.notes && <p className="mt-0.5 text-sm text-ink-400 dark:text-night-faint">{ev.notes}</p>}
        {ev.linkedTodoId && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-plum-100 px-2 py-0.5 text-[10px] font-semibold text-plum-700 dark:bg-night-surface-raised dark:text-night-accent">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-2.5 w-2.5">
              <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Todo
          </span>
        )}
        <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
          von {ev.createdByName}
          {ev.recurrence && ev.recurrence !== "none" && (
            <>
              {" "}
              · {RECURRENCE_SHORT[ev.recurrence]}
              {ev.recurrenceCount != null && <> (endet nach {ev.recurrenceCount})</>}
            </>
          )}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2.5">
        <button onClick={onComment} aria-label="Kommentare" className="relative text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M4 5h16v11H8l-4 4V5Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {!!ev.commentCount && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-plum-700 px-1 text-[10px] font-bold text-cream-50">
              {ev.commentCount}
            </span>
          )}
        </button>
        <button onClick={onEdit} aria-label="Bearbeiten" className="text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button onClick={onRemove} aria-label="Löschen" className="text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </li>
  );
}
